import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { map, Observable } from 'rxjs';

import { environment } from '@environment';

import { AppLoaderData } from '../models/app-loader-data.model';
import { AppLoaderNavigationService } from '../services/app-loader-navigation.service';

export const appLoaderDataResolver: ResolveFn<AppLoaderData> = (route: ActivatedRouteSnapshot): Observable<AppLoaderData> => {
  const appLoaderNavigationService = inject(AppLoaderNavigationService);
  const code = route.parent?.paramMap.get('code');

  return appLoaderNavigationService.resolveNavigationItem(code).pipe(
    map(navigationItem => {
      // Build app loader data from navigation item and route info
      if (!navigationItem) {
        return {
          queryParams: route.queryParams,
          subPath: null,
          parentRoutePath: ''
        };
      }

      const navigationDescription = navigationItem.Description;

      // Full URI of the child application (may contain query parameters)
      const fullUri = environment.mfeUrls[navigationDescription] ?? navigationDescription;
      
      // Parse URI to separate base URL and existing query parameters
      const { baseUri, uriQueryParams } = parseUriAndQueryParams(fullUri);
      
      // Merge URI query parameters with route query parameters (route params take precedence)
      const allQueryParams = { ...uriQueryParams, ...route.queryParams };
      
      // Extract sub-path from the activated route, using the child route segments
      const subPath = extractSubPath(route);
      
      return {
        uri: baseUri,
        queryParams: allQueryParams,
        subPath,
        parentRoutePath: navigationItem.State ?? ''
      };
    })
  );
};

function parseUriAndQueryParams(fullUri: string): { baseUri: string; uriQueryParams: Record<string, string> } {
  if (!fullUri) {
    return { baseUri: '', uriQueryParams: {} };
  }

  const [baseUri, queryString] = fullUri.split('?');
  const uriQueryParams: Record<string, string> = {};

  if (queryString) {
    const params = new URLSearchParams(queryString);
    for (const [key, value] of params.entries()) {
      uriQueryParams[key] = value;
    }
  }

  return { baseUri, uriQueryParams };
}

function extractSubPath(route: ActivatedRouteSnapshot): string | null {
  // For the new nested structure, the wildcard route captures everything as route.url
  const segments = route.url;
  
  if (segments.length === 0) {
    // This is the empty path route (e.g., /next/app/engagements)
    return null;
  }
  
  // This is the wildcard route, so all segments are the sub-path
  const subPath = '/' + segments.map(segment => segment.path).join('/');
  return subPath;
}


